import cleanDeep from "clean-deep";
import { useForm } from "react-hook-form";
import { connect } from "react-redux";
import { Actions as COMPANYACTIONS } from "../../../store/actions/companyActions";
import { Actions as FSS_REPORT_ACTIONS } from "../../../store/actions/fssReportActions";

import { CheckCircleIcon, InformationCircleIcon } from "@heroicons/react/20/solid";
// import SickPaySchemeRow from "./sick-pay-scheme/SickPaySchemeRow";
import { getTaxYear, validateFloat } from "@/components/common/validation/Validators";
import Tooltip from "@/tooltip";
import { format } from "date-fns";
import { cloneDeep, orderBy } from "lodash";
import ReactSelect from "react-select";
import { useEffect } from "react";

const deMinimisAidOptions = [
	{
		label: "State Aid",
		options: [
			{ label: "Agriculture", value: "Agriculture" },
			{ label: "Aquaculture and Fisheries", value: "Aquaculture and Fisheries" },
			{ label: "Road Freight Transport", value: "Road Freight Transport" },
			{ label: "Industrial and All Other Sectors", value: "Industrial and All Other Sectors" },
		],
	},
	{ label: "Or", options: [{ label: "State Aid Does not Apply", value: undefined }] },
];

const multiSelectStyles = {
	multiValue: (base, state) => {
		return state.data.isFixed ? { ...base, backgroundColor: "gray" } : base;
	},
	multiValueLabel: (base, state) => {
		return state.data.isFixed ? { ...base, fontWeight: "bold", color: "white", paddingRight: 6 } : base;
	},
	multiValueRemove: (base, state) => {
		return state.data.isFixed ? { ...base, display: "none" } : base;
	},
};

const Payroll = ({
	paySchedules,
	payScheduleGetList,
	addPaySchedule,
	removePaySchedule,
	hasDepartments,
	addMultiOptionField,
	sickPaySchemes,
	allowances,
	allowanceGetList,
	updateCompanyDetails,
	currentOrg,
	settings,
	updateSetting,
	disabled,
	jurisdiction,
	firstYear,
	currency,
	listFssReports,
	fssReports,
}) => {
	const { handleSubmit, control, register, watch } = useForm();
	const {
		alwaysExactHours,
		exactOnTermination,
		useExactOverAverageHours,
		weeklyIncome,
		ignoreAdjustments,
		includeWeeklyBreakdownInPayslip,
		showBasicRate,
		showTILModal,
		compressedPayslipsView,
		stateAid,
		employmentAllowance,
		smallEmployer,
		apprenticeLevyPartition,
		showEmployerContributions,
	} = settings;

	const fractioniseHours = typeof settings?.fractioniseHours === "undefined" ? true : settings?.fractioniseHours;

	const taxYear = getTaxYear(format(new Date(), "yyyy-MM-dd"), jurisdiction);

	const nextTaxYear = employmentAllowance && (parseInt(employmentAllowance[0], 10) + 1).toString();

	const onSubmit = (data) => {
		updateCompanyDetails(
			cleanDeep(
				{
					org: String(currentOrg),
					sickPaySchemes: sickPaySchemes?.filter((scheme) => !!scheme.id),
					alwaysExactHours,
					exactOnTermination,
					useExactOverAverageHours,
					weeklyIncome,
					ignoreAdjustments,
					includeWeeklyBreakdownInPayslip,
					apprenticeLevyPartition,
					showTILModal,
					compressedPayslipsView,
					showEmployerContributions,
					stateAid,
					employmentAllowance,
					smallEmployer,
					showBasicRate,
					fractioniseHours,
					enable: settings.enable,
				},
				{ emptyStrings: false, emptyArrays: false, emptyObjects: false },
			),
		);
	};

	useEffect(() => {
		//Return if jurisdiction is not UK
		if (jurisdiction != "UK") return;

		//If there is no fssReports or the last one is not from the current year or type is not p32, fetch the fssReports
		if (
			!fssReports || fssReports?.length == 0 ||
			fssReports[fssReports?.length - 1]?.year != employmentAllowance[0] ||
			fssReports[fssReports?.length - 1]?.type != "p32"
		) {
			listFssReports({ name: "p32", year: employmentAllowance && employmentAllowance[0] });
		}
	}, []);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			style={{ paddingBottom: 30 }}
			className="px-8"
		>

			<div className="form-section col-xs-12">
				<div className="col-xs-12">
					<div className="pull-left">
						<h4>Payroll Calculation</h4>
					</div>
				</div>
				<div className="col-xs-12 form-container">
					<label className="css-input switch switch-sm switch-success">
						<input
							ref={register}
							name="alwaysExactHours"
							checked={alwaysExactHours}
							type="checkbox"
							disabled={disabled}
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						When using timesheets always pay exact hours and not average hours. i.e. Do not use 173.33 hours
						for monthly payslips.
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							ref={register}
							name="exactOnTermination"
							checked={exactOnTermination}
							type="checkbox"
							disabled={disabled}
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Pay newly engaged or terminated employees based on actual hours worked, not on an average
						calculation.
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							ref={register}
							name="useExactOverAverageHours"
							checked={useExactOverAverageHours}
							type="checkbox"
							disabled={disabled}
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						<Tooltip content="Target hours are calculated based off of the days calculated by the Pay Schedule by default. Enable this to calculate based off employee's working days">
							When using AccuPay™, Calculate Target Hours Based on Employee's Working Days
						</Tooltip>
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							ref={register}
							name="fractioniseHours"
							checked={fractioniseHours}
							type="checkbox"
							disabled={disabled}
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						<Tooltip content="Convert Hours Ending with .33 to .3333333333 & .67 to .6666666667">
							Round .33 hours to 1/3 and .67 hours to 2/3
						</Tooltip>
					</label>
					{ jurisdiction == "UK" && (
						<label className="css-input switch switch-sm switch-success">
							<input
								ref={register}
								name="enable.backpay"
								checked={settings?.enable?.backpay}
								type="checkbox"
								disabled={disabled}
								onChange={(e) => updateSetting(e.target.name, e.target.checked)}
							/>
							<span />
							<Tooltip content="Calculate Backpay when a retroactive change is made to an employee's pay. This feature is currently in Beta">
								Calculate Backpay <sup>Beta</sup> when a retroactive change is made to an employee's pay
							</Tooltip>
						</label>
					)}
				</div>
				<div className="col-xs-12">
					<div className="pull-left">
						<h4>Payslip</h4>
					</div>
				</div>
				<div className="col-xs-12 form-container">
					<label className="css-input switch switch-sm switch-success">
						<input
							ref={register}
							name="includeWeeklyBreakdownInPayslip"
							checked={includeWeeklyBreakdownInPayslip}
							type="checkbox"
							disabled={disabled}
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Show the weekly hours summary on the payslip when using AccuPay™
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							ref={register}
							name="compressedPayslipsView"
							checked={compressedPayslipsView}
							type="checkbox"
							disabled={disabled}
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Use Compressed Payslips View
						<a
							href="https://support.buddy.hr/hc/en-mt/articles/12411529972498"
							target="_blank"
							className="h-7 w-7"
						>
							<Tooltip content="The Compressed Payslip layout is ideal if you have many pay items on your payslips, as more items can fit. Click to compare the two payslip layouts.">
								<InformationCircleIcon className="h-5 w-5" />
							</Tooltip>
						</a>
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							ref={register}
							name="showBasicRate"
							checked={showBasicRate}
							type="checkbox"
							disabled={disabled}
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Show the hourly rate on payslip {jurisdiction == "UK" ? "pay items" : "allowances"}
					</label>
					{jurisdiction == "MT" && (
						<label className="css-input switch switch-sm switch-success">
							<input
								ref={register}
								name="showEmployerContributions"
								checked={showEmployerContributions}
								type="checkbox"
								disabled={disabled}
								onChange={(e) => updateSetting(e.target.name, e.target.checked)}
							/>
							<span />
							Show Employer Contributions on Payslip
						</label>
					)}
				</div>
				{jurisdiction == "MT" && (
					<>
						<div className="col-xs-12">
							<div className="pull-left">
								<h4>FS3/FS7</h4>
							</div>
						</div>
						<div className="col-xs-12 form-container">
							<label className="css-input switch switch-sm switch-success">
								<input
									ref={register}
									name="weeklyIncome"
									checked={weeklyIncome}
									type="checkbox"
									disabled={disabled}
									onChange={(e) => updateSetting(e.target.name, e.target.checked)}
								/>
								<span />
								When generating FS3 for Imported Data re-calculate weekly wage based on income. If
								switched off, system will use weekly wage available in Salary options.
							</label>
							<label className="css-input switch switch-sm switch-success">
								<input
									ref={register}
									name="ignoreAdjustments"
									checked={ignoreAdjustments}
									type="checkbox"
									disabled={disabled}
									onChange={(e) => updateSetting(e.target.name, e.target.checked)}
								/>
								<span />
								When generating FS3, FS5 and FS7 summary data ignore Social Security Contributions and
								Maternity Fund Contributions from any calculation.
							</label>
						</div>
					</>
				)}
				{jurisdiction == "UK" && (
					<>
						<div className="col-xs-12">
							<div className="pull-left">
								<h4>Tax Reliefs</h4>
							</div>
						</div>
						<div className="col-xs-12 form-container">
							<label className="css-input switch switch-sm switch-success">
								<input
									checked={employmentAllowance?.includes(`${taxYear.year}`)}
									type="checkbox"
									disabled={disabled}
									onChange={(e) => {
										const year = taxYear.year;
										if (e.target.checked) {
											updateSetting("employmentAllowance", [
												...(employmentAllowance || []),
												`${year}`,
											]);
										} else {
											updateSetting(
												"employmentAllowance",
												employmentAllowance?.filter((y) => y != year),
											);
										}
									}}
								/>
								<span /> Claim Employment Allowance for {taxYear.year}/{parseFloat(taxYear.year) + 1}
							</label>
							<div className="form-group col-xs-6 pl-0">
								<label className="form-label">Employment Allowance Claimed</label>
								<ReactSelect
									placeholder="No Years Claimed"
									isDisabled={disabled}
									isClearable={false}
									styles={multiSelectStyles}
									value={employmentAllowance?.map((year) => ({
										label: `${year}/${parseFloat(year) + 1}`,
										value: `${year}`,
										isFixed: true,
									}))}
									isMulti
								/>
							</div>
							<div className="form-group col-xs-6 pl-0">
								<label className="form-label">State Aid</label>
								<ReactSelect
									options={deMinimisAidOptions}
									placeholder="No De Minimis Aid"
									isClearable={!!stateAid}
									onChange={(option) => updateSetting("stateAid", option?.value || null)}
									isDisabled={disabled}
									value={deMinimisAidOptions
										.flatMap(({ options }) => options)
										.find((aid) => aid.value == stateAid)}
								/>
							</div>
							<div className="form-material col-xs-6 pl-0">
								<label className="form-label">
									Employment Allowance to date &nbsp;
									<span className="help-block">{`(${employmentAllowance && employmentAllowance[0]}/${nextTaxYear})`}</span>
									<Tooltip
										as="span"
										content={(() => {
											return "This field is processed by Buddy, please contact support if you need to change it.";
										})()}
									>
										<InformationCircleIcon
											className="h-5 w-5"
											style={{ verticalAlign: "middle" }}
										/>
									</Tooltip>
								</label>
								<div style={{ position: "absolute", top: 8 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem " }}
									className="form-control form-input"
									id="employmentAllowance"
									name="employmentAllowance"
									disabled={true}
									value={fssReports && fssReports[fssReports?.length - 1]?.employmentAllowance}
								/>
							</div>
							<div className="form-material col-xs-6">
								<label className="form-label">
									Small Employer's Relief &nbsp;{" "}
									<span className="help-block">
										(NIC {firstYear}/{firstYear + 1})
									</span>
									<Tooltip
										as="span"
										content={(() => {
											if (settings?.smallEmployerProcessed) {
												return "This field has been processed by Buddy, please contact support if you need to change it.";
											}
											return "Small Employers' Relief qualifies if you paid £45,000 or less in Class 1 National Insurance (ignoring any reductions like Employment Allowance) in the last complete tax year before. Buddy requires the amount paid before your first year for accurate calculations";
										})()}
									>
										<InformationCircleIcon
											className="h-5 w-5"
											style={{ verticalAlign: "middle" }}
										/>
									</Tooltip>
								</label>
								<div style={{ position: "absolute", top: 8 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control form-input"
									id="smallEmployer"
									name="smallEmployer"
									value={smallEmployer}
									onChange={(e) => {
										updateSetting(e.target.name, validateFloat(e.target.value));
									}}
									placeholder={`Enter NI contributions for ${firstYear}/${firstYear + 1}`}
									autoComplete="off"
									disabled={settings?.smallEmployerProcessed || disabled}
								/>
							</div>
						</div>
						<div className="col-xs-12">
							<div className="pull-left">
								<h4>Apprenticeship Levy</h4>
							</div>
						</div>
						<div className="col-xs-12 form-container mt-5">
							<div className="form-material col-xs-12 col-sm-6 col-lg-3 pl-0">
								<label className="form-label">
									Partitioned Apprenticeship Allowance{" "}
									<Tooltip
										as="span"
										content={
											"You can decide how to split the allowance between your PAYE schemes or with your connected companies or charities. Enter the amount you want to allocate to this company."
										}
									>
										<InformationCircleIcon
											className="h-5 w-5"
											style={{ verticalAlign: "middle" }}
										/>
									</Tooltip>
								</label>
								<div style={{ position: "absolute", top: 8 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control form-input"
									id="apprenticeLevyPartition"
									name="apprenticeLevyPartition"
									value={apprenticeLevyPartition}
									placeholder="0 - 15,000"
									aria-invalid={
										apprenticeLevyPartition < 0 || apprenticeLevyPartition > 15000
											? "true"
											: "false"
									}
									onChange={(e) => {
										const value = validateFloat(e.target.value);
										const valueParsed = parseFloat(value);
										if (valueParsed < 0 || valueParsed > 15000) {
											return;
										}
										updateSetting(e.target.name, value);
									}}
									autoComplete="off"
									disabled={disabled}
								/>
							</div>
						</div>
						<div className="col-xs-12">
							<div className="pull-left">
								<h4>RTI</h4>
							</div>
						</div>
						<div className="col-xs-12 form-container">
							<label className="css-input switch switch-sm switch-success">
								<input
									ref={register}
									name="showTILModal"
									checked={showTILModal}
									type="checkbox"
									disabled={disabled}
									onChange={(e) => updateSetting(e.target.name, e.target.checked)}
								/>
								<span />
								When submitting an RTI, get asked whether you would like to submit it as a test or not.
							</label>
						</div>
					</>
				)}
			</div>

			{jurisdiction === "UK" && false && (
				<div className="form-section col-xs-12">
					<div className="col-xs-12">
						<div className="pull-left">
							<h4>Historic Payroll Data</h4>
						</div>
					</div>
					<div className="col-xs-12 pl-0 pb-6"></div>
				</div>
			)}

			<div className="clearfix" />

			{/* {jurisdiction == "UK" &&
				<div className="col-xs-12 form-section">
				<div className="col-xs-12 form-container">
					<div className="pull-left">
					<h4>Sick Pay Schemes</h4>
					</div>
					<div className="pull-right">
					{!disabled && hasDepartments ?
						<button
						className="btn btn-success pull-right"
						type="button"
						id="add-allowance-type"
						onClick={() => {
							addMultiOptionField('sickPaySchemes')
						}}
						>
						<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
						<div style={{ float: "left" }}>Add Sick Pay Scheme</div>
						</button>
					:null}
					</div>
				</div>
				{sickPaySchemes?.map((sickPayScheme) => <SickPaySchemeRow sickPayScheme={sickPayScheme} />)}
				</div>
			} */}
			<div className="clearfix" />
			<div className="col-xs-12 pr-0 text-right">
				{!disabled && (
					<button
						className="btn btn-success"
						type="submit"
					>
						<CheckCircleIcon style={{ height: "1.5em", float: "left" }} />
						<div style={{ float: "left" }}>Save</div>
					</button>
				)}
			</div>
		</form>
	);
};

const mapStateToProps = (state) => {
	const settings = cloneDeep(state.company?.settings);

	if (typeof settings?.showEmployerContributions == "undefined") {
		settings.showEmployerContributions = true;
	}

	return {
		sickPaySchemes: state.company?.settings?.sickPaySchemes,
		jurisdiction: state.company?.settings?.jurisdiction,
		paySchedules: state.company?.settings?.paySchedules,
		allowances: state.company?.settings?.allowances,
		hasDepartments: state.company?.settings?.departments?.length > 0,
		currentOrg: state.company?.settings?.org,
		settings,
		firstYear: orderBy(state.payPeriods?.periods || [], "year", "asc")?.[0]?.year - 1,
		disabled:
			state?.permissions?.my?.permissions?.settings == "Read" || state?.company?.settings?.status == "Archived",
		currency: state.company?.settings?.currency,
		fssReports: state?.fssReports,
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateSetting: (field, newValue) => dispatch(COMPANYACTIONS.updateSetting(field, newValue, false)),
	addMultiOptionField: (field) =>
		dispatch(COMPANYACTIONS.addMultiOptionField({ parent: null, newCompany: false, field })),
	updateCompanyDetails: (body) => dispatch(COMPANYACTIONS.updateCompanyDetails(body)),
	listFssReports: (data) => dispatch(FSS_REPORT_ACTIONS.listFssReports(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Payroll);
